import type { ReactNode } from "react";
import { Link } from "react-router-dom";

/** Shared chrome for the public legal pages (Terms, Privacy). Mirrors
 * the LandingPage header/footer so the surfaces feel like one site.
 * These pages are linked as the ToS / privacy URLs on the Microsoft
 * Entra app registration, so they must stay publicly reachable. */
export function LegalShell({
  title,
  lastUpdated,
  children,
}: {
  title: string;
  lastUpdated: string;
  children: ReactNode;
}) {
  return (
    <div className="absolute inset-0 overflow-y-auto bg-canvas" data-testid="legal-page">
      <header className="flex items-center justify-between border-b border-border-subtle bg-canvas px-6 py-5 md:px-20">
        <Link to="/" className="flex items-center" aria-label="BeneCRM home">
          <img src="/brand/benecrm-logo.png" alt="BeneCRM" className="h-7 w-auto md:h-8" />
        </Link>
        <Link
          to="/"
          className="text-sm font-medium text-text-secondary transition-fast ease-out hover:text-text-primary"
        >
          Back to home
        </Link>
      </header>

      <main className="mx-auto max-w-[760px] px-6 py-16 md:px-0">
        <h1
          className="font-serif text-[40px] italic leading-[1.1] text-text-primary md:text-[52px]"
          style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
        >
          {title}
        </h1>
        <p className="mt-3 text-[13px] text-text-tertiary">Last updated: {lastUpdated}</p>
        <div className="mt-5 rounded-md border border-border-subtle bg-raised px-4 py-3 text-[13px] leading-[1.5] text-text-secondary">
          Placeholder draft — not final legal copy. Replace with counsel-reviewed
          text before launch and before submitting the integration for enterprise review.
        </div>
        <div className="mt-10 flex flex-col gap-8 text-[15px] leading-[1.7] text-text-secondary">
          {children}
        </div>
      </main>

      <footer className="border-t border-border-subtle bg-canvas px-6 py-8 md:px-32">
        <div className="flex gap-6 text-[13px] text-text-secondary">
          <Link to="/privacy" className="hover:text-text-primary">Privacy</Link>
          <Link to="/terms" className="hover:text-text-primary">Terms</Link>
          <a href="mailto:hello@benecrm.com" className="hover:text-text-primary">Contact</a>
        </div>
        <p className="mt-3 text-xs text-text-tertiary">© 2026 BeneCRM. All rights reserved.</p>
      </footer>
    </div>
  );
}

export function LegalSection({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[18px] font-semibold text-text-primary">{heading}</h2>
      {children}
    </section>
  );
}
